import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import {
  AD_CARDS_ALLOWED_TYPES,
  AD_CARDS_BUCKET,
  AD_CARDS_MAX_ACTIVE,
  AD_CARDS_MAX_BYTES,
  buildAdCardStoragePath,
  dateInputToYmd,
  getPublicImageUrl,
  isEffectivelyActive,
  isExpiredByEndDate,
  normalizeLinkUrl,
  todayYmdSeoul,
  ymdToDateInput,
  AD_CARD_LINK_MENU_OPTIONS,
} from './adCardUtils';

const emptyForm = {
  title: '',
  description: '',
  link_url: '',
  link_menu: '',
  menu_shortcut: 'Y',
  startDate: '',
  endDate: '',
  is_active: 'N',
  sort_no: 0,
  storage_path: '',
  file_name: '',
  detail_storage_path: '',
  detail_file_name: '',
};

const AdCardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;
  const cardFileInputRef = useRef(null);
  const detailFileInputRef = useRef(null);

  const [form, setForm] = useState(emptyForm);
  const [previewUrl, setPreviewUrl] = useState('');
  const [detailPreviewUrl, setDetailPreviewUrl] = useState('');
  const [pendingCardFile, setPendingCardFile] = useState(null);
  const [pendingDetailFile, setPendingDetailFile] = useState(null);
  const [removedCardStoragePath, setRemovedCardStoragePath] = useState(null);
  const [removedDetailStoragePath, setRemovedDetailStoragePath] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (isNew) {
      setForm(emptyForm);
      setPreviewUrl('');
      setDetailPreviewUrl('');
      setPendingCardFile(null);
      setPendingDetailFile(null);
      setRemovedCardStoragePath(null);
      setRemovedDetailStoragePath(null);
      setLoading(false);
      return;
    }
    loadData();
  }, [id, isNew]);

  const loadData = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const today = todayYmdSeoul();
      await supabase
        .from('ad_cards')
        .update({ is_active: 'N' })
        .eq('id', id)
        .eq('is_active', 'Y')
        .lt('end_ymd', today);

      const { data, error } = await supabase
        .from('ad_cards')
        .select(
          'id, title, description, link_url, link_menu, menu_shortcut, storage_path, file_name, detail_storage_path, detail_file_name, start_ymd, end_ymd, is_active, sort_no, updated_at'
        )
        .eq('id', id)
        .single();
      if (error) throw error;

      const cacheKey = data.updated_at || Date.now();
      const hasDetailImage = Boolean(data.detail_storage_path);
      setForm({
        title: data.title || '',
        description: data.description || '',
        link_url: data.link_url || '',
        link_menu: data.link_menu || '',
        menu_shortcut: hasDetailImage ? 'N' : data.menu_shortcut === 'N' ? 'N' : 'Y',
        startDate: ymdToDateInput(data.start_ymd),
        endDate: ymdToDateInput(data.end_ymd),
        is_active: data.is_active || 'N',
        sort_no: data.sort_no ?? 0,
        storage_path: data.storage_path || '',
        file_name: data.file_name || '',
        detail_storage_path: data.detail_storage_path || '',
        detail_file_name: data.detail_file_name || '',
      });
      setPreviewUrl(data.storage_path ? getPublicImageUrl(supabase, data.storage_path, cacheKey) : '');
      setDetailPreviewUrl(
        data.detail_storage_path ? getPublicImageUrl(supabase, data.detail_storage_path, cacheKey) : ''
      );
      setPendingCardFile(null);
      setPendingDetailFile(null);
      setRemovedCardStoragePath(null);
      setRemovedDetailStoragePath(null);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '데이터 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const hasDetailImage = () =>
    Boolean(pendingDetailFile || form.detail_storage_path || detailPreviewUrl);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateImageFile = (file) => {
    if (!AD_CARDS_ALLOWED_TYPES.includes(file.type)) {
      return 'PNG, JPEG, WEBP 이미지만 업로드할 수 있습니다.';
    }
    if (file.size > AD_CARDS_MAX_BYTES) {
      return `이미지 용량은 ${AD_CARDS_MAX_BYTES / 1024}KB 이내여야 합니다. (현재 ${Math.ceil(file.size / 1024)}KB)`;
    }
    return null;
  };

  const rejectImageFile = (err) => {
    if (!err) return false;
    window.alert(err);
    setMessage({ type: 'error', text: err });
    return true;
  };

  const handleCardFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const err = validateImageFile(file);
    if (rejectImageFile(err)) return;
    setPendingCardFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setRemovedCardStoragePath(null);
    setForm((prev) => ({ ...prev, file_name: file.name }));
    setMessage(null);
  };

  const handleDetailFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const err = validateImageFile(file);
    if (rejectImageFile(err)) return;
    setPendingDetailFile(file);
    setDetailPreviewUrl(URL.createObjectURL(file));
    setRemovedDetailStoragePath(null);
    setForm((prev) => ({ ...prev, detail_file_name: file.name, menu_shortcut: 'N' }));
    setMessage(null);
  };

  const handleRemoveCardImage = () => {
    if (!previewUrl && !form.storage_path && !pendingCardFile) return;
    if (!window.confirm('광고 카드 이미지를 삭제할까요?')) return;
    if (form.storage_path) setRemovedCardStoragePath(form.storage_path);
    setPendingCardFile(null);
    setPreviewUrl('');
    setForm((prev) => ({ ...prev, storage_path: '', file_name: '' }));
    setMessage(null);
  };

  const handleRemoveDetailImage = () => {
    if (!detailPreviewUrl && !form.detail_storage_path && !pendingDetailFile) return;
    if (!window.confirm('광고 상세 이미지를 삭제할까요?')) return;
    if (form.detail_storage_path) setRemovedDetailStoragePath(form.detail_storage_path);
    setPendingDetailFile(null);
    setDetailPreviewUrl('');
    setForm((prev) => ({
      ...prev,
      detail_storage_path: '',
      detail_file_name: '',
      menu_shortcut: 'Y',
    }));
    setMessage(null);
  };

  const handleMenuShortcutChange = (event) => {
    const checked = event.target.checked;
    if (checked) {
      if (hasDetailImage()) {
        window.alert('상세 화면 적용 광고입니다. 바로가기를 하시려면 상세 이미지를 삭제해 주세요');
        return;
      }
      setForm((prev) => ({ ...prev, menu_shortcut: 'Y' }));
      return;
    }
    setForm((prev) => ({ ...prev, menu_shortcut: 'N' }));
  };

  const countActiveExcludingSelf = async (excludeId) => {
    const today = todayYmdSeoul();
    let query = supabase
      .from('ad_cards')
      .select('id', { count: 'exact', head: true })
      .eq('is_active', 'Y')
      .gte('end_ymd', today);
    if (excludeId) query = query.neq('id', excludeId);
    const { count, error } = await query;
    if (error) throw error;
    return count || 0;
  };

  const validate = () => {
    if (!form.title.trim()) {
      setMessage({ type: 'error', text: '광고 타이틀을 입력해주세요.' });
      return false;
    }
    const startYmd = dateInputToYmd(form.startDate);
    const endYmd = dateInputToYmd(form.endDate);
    if (startYmd.length !== 8) {
      setMessage({ type: 'error', text: '게시일을 선택해주세요.' });
      return false;
    }
    if (endYmd.length !== 8) {
      setMessage({ type: 'error', text: '게시 종료일을 선택해주세요.' });
      return false;
    }
    if (startYmd > endYmd) {
      setMessage({ type: 'error', text: '게시 종료일은 게시일 이후여야 합니다.' });
      return false;
    }
    if (form.link_url.trim() && !normalizeLinkUrl(form.link_url)) {
      setMessage({ type: 'error', text: '외부 링크는 http:// 또는 https:// 로 시작하는 URL을 입력해주세요.' });
      return false;
    }
    if (isNew && !pendingCardFile && !form.storage_path) {
      setMessage({ type: 'error', text: '광고 카드 이미지를 선택해주세요.' });
      return false;
    }
    return true;
  };

  const uploadImageIfNeeded = async (cardId, pendingFile, previousPath, kind, removedPath) => {
    const isCard = kind === 'card';
    if (!pendingFile) {
      if (removedPath) {
        await supabase.storage.from(AD_CARDS_BUCKET).remove([removedPath]);
        return isCard
          ? { storage_path: null, file_name: null }
          : { detail_storage_path: null, detail_file_name: null };
      }
      return isCard
        ? { storage_path: form.storage_path || null, file_name: form.file_name || null }
        : { detail_storage_path: form.detail_storage_path || null, detail_file_name: form.detail_file_name || null };
    }
    const uploadPath = buildAdCardStoragePath(cardId, pendingFile.name, kind);
    const { error: uploadError } = await supabase.storage
      .from(AD_CARDS_BUCKET)
      .upload(uploadPath, pendingFile, {
        upsert: true,
        contentType: pendingFile.type,
      });
    if (uploadError) throw uploadError;

    if (previousPath && previousPath !== uploadPath) {
      await supabase.storage.from(AD_CARDS_BUCKET).remove([previousPath]);
    }

    return isCard
      ? { storage_path: uploadPath, file_name: pendingFile.name }
      : { detail_storage_path: uploadPath, detail_file_name: pendingFile.name };
  };

  const buildPayload = (cardImageFields, detailImageFields, isActive) => {
    const hasDetail = Boolean(detailImageFields.detail_storage_path);
    const menuShortcut = hasDetail ? 'N' : form.menu_shortcut === 'Y' ? 'Y' : 'N';
    return {
      title: form.title.trim(),
      description: form.description.trim(),
      link_url: normalizeLinkUrl(form.link_url),
      link_menu: form.link_menu.trim() || null,
      menu_shortcut: menuShortcut,
      start_ymd: dateInputToYmd(form.startDate),
      end_ymd: dateInputToYmd(form.endDate),
      is_active: isActive,
      sort_no: Number.isFinite(Number(form.sort_no)) ? Number(form.sort_no) : 0,
      ...cardImageFields,
      ...detailImageFields,
    };
  };

  const ensureCanActivate = async (excludeId, endYmd) => {
    if (isExpiredByEndDate(endYmd)) {
      throw new Error('게시 종료일이 지나 활성화할 수 없습니다. 종료일을 변경한 뒤 다시 시도해주세요.');
    }
    const activeCount = await countActiveExcludingSelf(excludeId);
    if (activeCount >= AD_CARDS_MAX_ACTIVE) {
      throw new Error(`활성화된 광고는 최대 ${AD_CARDS_MAX_ACTIVE}개까지 가능합니다. (현재 ${activeCount}개)`);
    }
  };

  const handleSave = async (nextActive = form.is_active) => {
    if (!validate()) return;
    setSaving(true);
    setMessage(null);
    try {
      const endYmd = dateInputToYmd(form.endDate);
      let activeFlag = nextActive === 'Y' ? 'Y' : 'N';
      if (activeFlag === 'Y') {
        await ensureCanActivate(isNew ? null : id, endYmd);
      }
      if (isExpiredByEndDate(endYmd)) {
        activeFlag = 'N';
      }

      if (isNew) {
        const { data: created, error: insertError } = await supabase
          .from('ad_cards')
          .insert([
            buildPayload(
              { storage_path: null, file_name: null },
              { detail_storage_path: null, detail_file_name: null },
              'N'
            ),
          ])
          .select('id')
          .single();
        if (insertError) throw insertError;

        const cardImageFields = await uploadImageIfNeeded(
          created.id,
          pendingCardFile,
          null,
          'card',
          removedCardStoragePath
        );
        const detailImageFields = await uploadImageIfNeeded(
          created.id,
          pendingDetailFile,
          null,
          'detail',
          removedDetailStoragePath
        );
        const { error: updateError } = await supabase
          .from('ad_cards')
          .update(buildPayload(cardImageFields, detailImageFields, activeFlag))
          .eq('id', created.id);
        if (updateError) throw updateError;

        setRemovedCardStoragePath(null);
        setRemovedDetailStoragePath(null);
        navigate(`/ad-cards/${created.id}`, { replace: true });
        setMessage({ type: 'success', text: '저장되었습니다.' });
      } else {
        const cardImageFields = await uploadImageIfNeeded(
          id,
          pendingCardFile,
          form.storage_path,
          'card',
          removedCardStoragePath
        );
        const detailImageFields = await uploadImageIfNeeded(
          id,
          pendingDetailFile,
          form.detail_storage_path,
          'detail',
          removedDetailStoragePath
        );
        const payload = buildPayload(cardImageFields, detailImageFields, activeFlag);
        const { error } = await supabase.from('ad_cards').update(payload).eq('id', id);
        if (error) throw error;
        setForm((prev) => ({
          ...prev,
          is_active: activeFlag,
          link_url: payload.link_url || '',
          link_menu: payload.link_menu || '',
          menu_shortcut: payload.menu_shortcut,
          storage_path: cardImageFields.storage_path || '',
          file_name: cardImageFields.file_name || '',
          detail_storage_path: detailImageFields.detail_storage_path || '',
          detail_file_name: detailImageFields.detail_file_name || '',
        }));
        setPendingCardFile(null);
        setPendingDetailFile(null);
        setRemovedCardStoragePath(null);
        setRemovedDetailStoragePath(null);
        const cacheKey = Date.now();
        if (cardImageFields.storage_path) {
          setPreviewUrl(getPublicImageUrl(supabase, cardImageFields.storage_path, cacheKey));
        } else {
          setPreviewUrl('');
        }
        if (detailImageFields.detail_storage_path) {
          setDetailPreviewUrl(getPublicImageUrl(supabase, detailImageFields.detail_storage_path, cacheKey));
        } else {
          setDetailPreviewUrl('');
        }
        setMessage({ type: 'success', text: '수정되었습니다.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '저장 중 오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    const next = form.is_active === 'Y' ? 'N' : 'Y';
    await handleSave(next);
  };

  const handleDelete = async () => {
    if (isNew) return;
    if (!window.confirm('이 광고 카드를 삭제할까요?')) return;
    setSaving(true);
    setMessage(null);
    try {
      const pathsToRemove = [form.storage_path, form.detail_storage_path].filter(Boolean);
      if (pathsToRemove.length) {
        await supabase.storage.from(AD_CARDS_BUCKET).remove(pathsToRemove);
      }
      const { error } = await supabase.from('ad_cards').delete().eq('id', id);
      if (error) throw error;
      navigate('/ad-cards');
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '삭제 중 오류가 발생했습니다.' });
      setSaving(false);
    }
  };

  const today = todayYmdSeoul();
  const effectivelyActive = isEffectivelyActive(
    { is_active: form.is_active, end_ymd: dateInputToYmd(form.endDate) },
    today
  );
  const activeLabel = effectivelyActive ? '활성화' : '비활성화';
  const activeBg = effectivelyActive ? '#28a745' : '#dc3545';

  const renderImageField = ({
    label,
    helpText,
    previewUrl: imgUrl,
    fileName,
    pendingFile,
    onPick,
    onRemove,
    inputRef,
    onChange,
  }) => {
    const canRemove = Boolean(imgUrl || fileName || pendingFile);
    return (
    <div className="form-group">
      <label>{label}</label>
      {helpText ? <p className="small text-muted mb-2">{helpText}</p> : null}
      <div className="d-flex align-items-start flex-wrap" style={{ gap: 16 }}>
        <div
          style={{
            width: 240,
            minHeight: 140,
            borderRadius: 8,
            overflow: 'hidden',
            background: '#e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {imgUrl ? (
            <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <span className="text-muted small">이미지 없음</span>
          )}
        </div>
        <div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={onPick} disabled={saving}>
              이미지 선택
            </button>
            {canRemove ? (
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={onRemove} disabled={saving}>
                삭제
              </button>
            ) : null}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={onChange}
          />
          <div className="small text-muted mt-2">
            {fileName || 'PNG / JPEG / WEBP'}
            {pendingFile ? ` · 선택됨 (${Math.ceil(pendingFile.size / 1024)}KB)` : ''}
          </div>
        </div>
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <div className="card shadow mb-4">
        <div className="card-body">조회 중...</div>
      </div>
    );
  }

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
        <h6 className="m-0 font-weight-bold text-primary">
          {isNew ? '광고 카드 등록' : '광고 카드 상세'}
        </h6>
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate('/ad-cards')}
            disabled={saving}
          >
            목록
          </button>
          {!isNew && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={handleDelete}
              disabled={saving}
            >
              삭제
            </button>
          )}
          <button
            type="button"
            className={`btn btn-sm ${effectivelyActive ? 'btn-warning' : 'btn-success'}`}
            onClick={handleToggleActive}
            disabled={saving}
          >
            {saving ? '처리 중...' : effectivelyActive ? '비활성화' : '활성화'}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => handleSave(form.is_active)}
            disabled={saving}
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {message && (
          <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`} role="alert">
            {message.text}
          </div>
        )}

        <div className="form-group">
          <label>광고 타이틀</label>
          <input
            type="text"
            className="form-control"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="광고 타이틀"
          />
        </div>

        <div className="form-group">
          <label>광고 설명</label>
          <textarea
            className="form-control"
            name="description"
            rows="4"
            value={form.description}
            onChange={handleChange}
            placeholder="광고 설명"
          />
        </div>

        {renderImageField({
          label: `광고 카드 이미지 (최대 ${AD_CARDS_MAX_BYTES / 1024}KB)`,
          helpText: 'today_qt 목록에 노출되는 카드 섬네일 이미지입니다.',
          previewUrl,
          fileName: form.file_name,
          pendingFile: pendingCardFile,
          onPick: () => cardFileInputRef.current?.click(),
          onRemove: handleRemoveCardImage,
          inputRef: cardFileInputRef,
          onChange: handleCardFileChange,
        })}

        <div className="form-group">
          <label>외부 링크</label>
          <input
            type="url"
            className="form-control"
            name="link_url"
            value={form.link_url}
            onChange={handleChange}
            placeholder="https://example.com"
          />
          <small className="form-text text-muted">
            앱에서 광고 카드 클릭 시 외부 브라우저로 열립니다. 비우면 링크 없음.
          </small>
        </div>

        <div className="form-group">
          <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 12 }}>
            <label className="mb-0">연결 메뉴</label>
            <label
              className="mb-0 d-flex align-items-center"
              style={{ gap: 6, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 'normal' }}
            >
              <input
                type="checkbox"
                checked={form.menu_shortcut === 'Y'}
                onChange={handleMenuShortcutChange}
                disabled={saving}
              />
              바로가기
            </label>
          </div>
          <select
            className="form-control"
            name="link_menu"
            value={form.link_menu}
            onChange={handleChange}
          >
            {AD_CARD_LINK_MENU_OPTIONS.map((opt) => (
              <option key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            바로가기가 켜져 있으면 앱에서 광고 카드 탭 시 연결 메뉴로 바로 이동합니다. 상세 이미지가 있으면 바로가기를 사용할 수 없습니다.
          </small>
        </div>

        {renderImageField({
          label: `광고 상세 이미지 (최대 ${AD_CARDS_MAX_BYTES / 1024}KB)`,
          helpText:
            '앱에서 광고 카드를 탭하면 광고 상세 페이지로 이동하며, 이 이미지가 표시됩니다. (선택)',
          previewUrl: detailPreviewUrl,
          fileName: form.detail_file_name,
          pendingFile: pendingDetailFile,
          onPick: () => detailFileInputRef.current?.click(),
          onRemove: handleRemoveDetailImage,
          inputRef: detailFileInputRef,
          onChange: handleDetailFileChange,
        })}

        <div className="form-row">
          <div className="form-group col-md-4">
            <label>게시일</label>
            <input
              type="date"
              className="form-control"
              name="startDate"
              value={form.startDate}
              onChange={handleChange}
            />
            <small className="form-text text-muted">저장: YYYYMMDD · 표시: YYYY-MM-DD</small>
          </div>
          <div className="form-group col-md-4">
            <label>게시 종료일</label>
            <input
              type="date"
              className="form-control"
              name="endDate"
              value={form.endDate}
              onChange={handleChange}
            />
            <small className="form-text text-muted">종료일 경과 시 자동 비활성화</small>
          </div>
          <div className="form-group col-md-4">
            <label>정렬 번호</label>
            <input
              type="number"
              className="form-control"
              name="sort_no"
              value={form.sort_no}
              onChange={handleChange}
              min={0}
            />
          </div>
        </div>

        <div className="form-group mb-0">
          <label>활성화 여부</label>
          <input
            type="text"
            className="form-control font-weight-bold text-white text-center"
            value={activeLabel}
            readOnly
            style={{
              backgroundColor: activeBg,
              border: 'none',
              maxWidth: 200,
            }}
          />
          <small className="form-text text-muted">
            DB 값: {form.is_active} · 활성 노출 상한 {AD_CARDS_MAX_ACTIVE}건
            {isExpiredByEndDate(dateInputToYmd(form.endDate), today) ? ' · 종료일 경과' : ''}
          </small>
        </div>
      </div>
    </div>
  );
};

export default AdCardDetail;
